import React, { useRef, useEffect, useState, useCallback, useContext } from 'react';
import { makeStyles, useTheme } from '@mui/styles';
import PropTypes from 'prop-types';

import Popover from '@mui/material/Popover';
import StyledPaper from './../styled/paper';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import HelpIcon from '@mui/icons-material/Help';

import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import SearchIcon from '@mui/icons-material/Search';

import { select as D3select } from 'd3';
import {
    cloneDeep as _cloneDeep,
    merge as _merge,
    debounce as _debounce,
    get as _get,
} from 'lodash';

import MetadataService from '../../data/metadata-service';
import DataContext from '../../data/data-context';
import D3SeaUrchin from '../../d3/D3SeaUrchin';
import { GetISODateFromQuick } from '../../libraries/helpers';

const useStyles = makeStyles(theme => ({
    root: {
        '& .node-title': {
            fontSize: 12,
            fill: theme.palette.text.primary
        }
    },
    itemIcon: {
        minWidth: 25,
    },
}));

const SeaUrchin = (props) => {
    const d3Container = useRef(null);
    const mainRef = useRef(null);
    const classes = useStyles();
    const theme = useTheme();
    const { mtdtMappings: mappings } = useContext(DataContext);

    const [data, setData] = useState({ breadcrumbs: [], children: [], connm: {}, links: [] });
    const [kick, setKick] = useState(false);
    const [width, setWidth] = useState(400);
    const [height, setHeight] = useState(300);
    const [currentPopup, setCurrentPopup] = useState();
    const [init, setInit] = useState(false);

    const buildGraph = useCallback((node, values, data) => {
        // update breadcrumbs
        if (node.breadcrumb) {
            const i = data.breadcrumbs.findIndex(b => b.id === node.id);
            if (i === data.breadcrumbs.length - 1) return;
            if (i < 0) {
                console.log('something went wrong');
                // start over
                return buildGraph({
                    sel: { ip: props.ip },
                    data: { breadcrumbs: [], children: [], connm: {}, links: [] },
                });
            }
            data.breadcrumbs.splice(i + 1);
        } else {
            data.breadcrumbs.push({
                id: data.breadcrumbs.length,
                ip: node.ip,
                key: node.ip,
                // asset: (brcAssets.read({ ip: node.ip }) || { name: node.ip }).name,
                asset: node.ip,
                count: values.reduce((a, d) => a + d.count, 0),
                breadcrumb: true,
                home: data.breadcrumbs.length < 1
            });
            if (!data.connm[node.ip]) data.connm[node.ip] = {};
        }

        // recreate links
        data.links = [];
        for (let i = 0; i < data.breadcrumbs.length - 1; i++) {
            data.links.push({
                source: data.breadcrumbs[i],
                target: data.breadcrumbs[i + 1],
                count: data.breadcrumbs[i + 1].count
            });
        }

        // add children nodes and links
        // children will include node's immediate parent. Filter that out.
        data.children = [];
        values
            .filter(v => data.breadcrumbs.length < 2 || data.breadcrumbs[data.breadcrumbs.length - 2].ip !== v.ip)
            .forEach((v, i) => {
                data.children.push({
                    id: data.breadcrumbs[data.breadcrumbs.length - 1].id + '.' + i,
                    ip: v.ip,
                    key: v.ip,
                    // asset: (brcAssets.read({ ip: v.ip }) || { name: v.ip }).name,
                    asset: v.ip,
                    count: v.count
                });
                data.links.push({
                    source: data.breadcrumbs[data.breadcrumbs.length - 1],
                    target: data.children[data.children.length - 1],
                    count: data.children[data.children.length - 1].count
                });
                data.connm[node.ip][v.ip] = true;
            });
        setData({ ...data });
    }, [props.ip]);

    const expandNode = useCallback(({ sel, data }, props) => {
        if (data.breadcrumbs.length > 0 && sel.id === data.breadcrumbs[data.breadcrumbs.length - 1].id) return;

        const p = _cloneDeep(props.params);

        // add current ip to filter
        const nodes = [{ value: '' + sel.ip, variable: 'ips', type: 'ip' }];
        p.filter = p.filter || { operator: 'And', nodes: [] };
        if (_get(p, 'filter.operator', '').toLowerCase() !== 'and') {
            p.filter = { operator: 'And', nodes: [p.filter, ...nodes] };
        } else {
            p.filter.nodes.push(...nodes);
        }

        p.start = p.quick ? GetISODateFromQuick(p.quick) : p.start;
        p.end = p.quick ? undefined : p.end;

        p.sort = 'count desc';
        p.groups = [{ variable: 'ips' }];

        // add mapped ip columns, let the service do the conversion
        if (MetadataService.isMapped('bro_log.id.orig_h')) {
            p.groups.push({ variable: 'bro_log.id.orig_h' });
        }
        if (MetadataService.isMapped('bro_log.id.resp_h')) {
            p.groups.push({ variable: 'bro_log.id.resp_h' });
        }

        p.limit = 22;
        // conn should always be added to the sea urchin
        if (!p.sources || !p.sources.find(s => 'conn')) {
            p.sources = [...[p.sources || []], 'conn'];
        }

        props.setBusy && props.setBusy(true);
        MetadataService.getGroups(p, false).then(res => {
            const list = (res.data || []).map(d => {
                return _merge({}, d, { ip: d.groups.length > 0 ? d.groups[0].value : '' });
            });
            buildGraph(sel, list.filter(d => d.ip !== sel.ip), data);
        }, err => {
            console.log('cannot get producers for', sel.ip, err);
        }).finally(() => {
            setKick(false);
            props.setBusy && props.setBusy(false);
        });
    }, [buildGraph, mappings]);

    const handleOnCick = (d, node) => {
        setCurrentPopup({ d, elm: node[0] });
    };

    const handleClose = () => {
        setCurrentPopup();
    };

    const handleCenter = () => {
        if (!currentPopup) return;
        setKick({ sel: currentPopup.d, data });
        setCurrentPopup();
    };

    const handleFilter = () => {
        if (!currentPopup) return;
        props.onFilter && props.onFilter({ type: 'ip', variable: 'ips', value: currentPopup.d.ip });
        setCurrentPopup();
    };

    const handleDblClick = e => {
        setKick({
            sel: { ip: props.ip },
            data: { breadcrumbs: [], children: [], connm: {}, links: [] },
        });
        event.preventDefault();
        event.stopPropagation();
    };

    const doResize = useCallback(() => {
        if (!mainRef.current) return;
        setWidth(Math.max(mainRef.current.offsetWidth, 500));
        setHeight(Math.max(mainRef.current.offsetHeight - 150, 300));
    }, []);

    // resize hook
    useEffect(() => {
        const resize = _debounce(doResize, 111);
        if (!props.size) {
            window.addEventListener('resize', resize);
        } else {
            window.removeEventListener('resize', resize);

            // take up as much room as you have
            setWidth(props.size.width || 300);
            setHeight(props.size.height || 300);
        }
        return () => {
            window.removeEventListener('resize', resize);
        };
    }, [props.size, doResize]);

    useEffect(() => {
        if (!init || !kick) return;
        expandNode(kick, { params: props.params, setBusy: props.setBusy });
    }, [init, kick, expandNode, props.params, props.setBusy]);

    useEffect(() => {
        if (!init) return;
        setKick({
            sel: { ip: props.ip },
            data: { breadcrumbs: [], children: [], connm: {}, links: [] },
        });
    }, [init, props.ip]);

    useEffect(() => {
        if (d3Container.current) {
            const node = d3Container.current;
            const nodes = (data.breadcrumbs || []).concat(data.children);

            D3select(node).selectAll('svg')
                .data([{
                    key: props.ip,
                    links: data.links,
                    nodes,
                    connm: data.connm
                }], function (d) { return d.key })
                .join('svg')
                // .attr('viewBox', '0 0 ' + width + ' ' + height)
                // .attr('preserveAspectRatio', 'xMidYMid meet')
                // .attr('style', 'border:1px solid black')
                .attr('width', width)
                .attr('height', height)
                .style('display', 'block')
                .style('margin', 'auto')
                .call(D3SeaUrchin()
                    .theme(theme)
                    .width(width)
                    .height(height)
                    .onClickHandler(handleOnCick)
                );
            setInit(true);
        }
    }, [width, height, data, theme, props.ip]);

    return (
        <div className={classes.root} ref={mainRef} onDoubleClick={handleDblClick}>
            <Tooltip
                title={
                    <React.Fragment>
                        <Typography color="inherit">Interactive Graph</Typography>
                        <ul style={{ fontSize: 12, listStyleType: 'none', paddingLeft: 0 }}>
                            <li>{'Click a node for context menu'}</li>
                            <li>{'Move a node to pin, shift + move to release'}</li>
                            <li>{'Double click to reset graph'}</li>
                        </ul>
                    </React.Fragment>
                }>
                <HelpIcon
                    color="secondary"
                    style={{ float: 'right', opacity: props.showhelp ? 1 : 0 }}/>
            </Tooltip>
            <div ref={d3Container}></div>
            {currentPopup && <Popover
                open={Boolean(currentPopup)}
                anchorEl={currentPopup.elm}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}>
                <StyledPaper className={classes.paper}>
                    <MenuItem onClick={handleCenter}>
                        <ListItemIcon className={classes.itemIcon}>
                            <CenterFocusStrongIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Center" />
                    </MenuItem>
                    <MenuItem onClick={handleFilter}>
                        <ListItemIcon className={classes.itemIcon}>
                            <SearchIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Filter" />
                    </MenuItem>
                </StyledPaper>
            </Popover>}
        </div>
    );
};

SeaUrchin.propTypes = {
    ip: PropTypes.string,
    params: PropTypes.object,
    onFilter: PropTypes.func,
    showhelp: PropTypes.bool,
    size: PropTypes.object, // if omitted will use window size - navbar
    setBusy: PropTypes.func,
};

export default SeaUrchin;
